import { useEffect, useRef, useState } from "react";
import { Textarea } from "../ui/textarea";
import {
  Star, Trash2, Pencil, GripVertical, ArrowUpRight, Play, FileText, ImageOff,
} from "lucide-react";
import { getLinkMeta } from "./linkMeta";
import { videoPosters, linkHost } from "./scopes";
import CoverImage from "./CoverImage";

/**
 * The contents of one department's scope, laid out the way that kind of work
 * wants to be seen — a mosaic for stills, play cards for animation, covers for
 * decks, a banner for a live platform.
 *
 * Rendered inside ScopeDialog, which owns the heading and the two "add
 * something" buttons; this owns the note and the work. The same component
 * renders the sales-facing view and the organize view — the only difference is
 * that organize mode adds the controls, so what a PM arranges is exactly what a
 * rep sees.
 */
export default function ScopeSection({
  scope,
  group,
  inPackage,
  organizing,
  note,
  onNoteSave,
  onUpload,
  onEditItem,
  onDeleteItem,
  onSetCover,
  onReorder,
  onOpenImage,
  heroImageId,
  mediaUrl,
}) {
  const [dropping, setDropping] = useState(false);
  const [noteDraft, setNoteDraft] = useState(note || "");

  useEffect(() => { setNoteDraft(note || ""); }, [note]);

  const { images, documents, links } = group;
  const total = images.length + documents.length + links.length;
  const acceptTypes = [
    scope.acceptsImages && "image/*",
    scope.acceptsDocuments && "application/pdf",
  ].filter(Boolean).join(",");
  const canUpload = Boolean(acceptTypes);

  // Each kind of item gets the presentation it deserves wherever it lands — a
  // video link is a play card whether it sits in Video or in Campaign. `layout`
  // only decides which block leads the section.
  const isFeature = scope.layout === "feature";
  const videoLinks = isFeature ? [] : links.filter(l => l.media_type === "video_link");
  const docLinks = isFeature ? [] : links.filter(l => l.media_type === "doc_link");
  const rowLinks = isFeature ? [] : links.filter(
    l => l.media_type !== "video_link" && l.media_type !== "doc_link"
  );
  // A feature banner wears the first screenshot, so the strip below starts at the second.
  const featureBackdrop = isFeature && links.length && images[0] ? images[0] : null;
  const mosaicImages = featureBackdrop ? images.slice(1) : images;

  const BLOCKS_BY_LAYOUT = {
    feature: ["feature", "images", "links"],
    video: ["video", "images", "documents", "links"],
    documents: ["documents", "images", "video", "links"],
    gallery: ["images", "video", "documents", "links"],
    files: ["links", "documents", "images"],
  };
  const blocks = BLOCKS_BY_LAYOUT[scope.layout] || BLOCKS_BY_LAYOUT.gallery;

  // Only file drags open the dropzone — an item being dragged into a new
  // position must not be mistaken for an upload.
  const isFileDrag = (e) => Array.from(e.dataTransfer?.types || []).includes("Files");

  const handleDrop = (e) => {
    if (!organizing || !canUpload || !isFileDrag(e)) return;
    e.preventDefault();
    setDropping(false);
    onUpload(scope.key, e.dataTransfer.files);
  };

  const itemActions = { onEditItem, onDeleteItem, onSetCover, heroImageId, organizing, mediaUrl };

  return (
    <section
      data-testid={`scope-section-${scope.key}`}
      onDragOver={(e) => { if (organizing && canUpload && isFileDrag(e)) { e.preventDefault(); setDropping(true); } }}
      onDragLeave={(e) => { if (e.currentTarget === e.target) setDropping(false); }}
      onDrop={handleDrop}
    >
      {/* Scope note — what this department delivered, in words */}
      {organizing ? (
        <Textarea
          value={noteDraft}
          onChange={(e) => setNoteDraft(e.target.value)}
          onBlur={() => { if ((noteDraft || "") !== (note || "")) onNoteSave(scope.key, noteDraft); }}
          rows={2}
          placeholder={`What did we deliver here? ${scope.blurb}`}
          className="mb-5 text-sm"
          data-testid={`note-${scope.key}`}
        />
      ) : note ? (
        <p className="body-text mb-5 max-w-3xl">{note}</p>
      ) : null}

      <div className={`rounded-xl transition-smooth ${dropping ? "outline-dashed outline-2 outline-offset-4 outline-primary/40" : ""}`}>
        {total === 0 ? (
          <EmptyScope scope={scope} organizing={organizing} inPackage={inPackage} />
        ) : (
          <div className="space-y-6">
            {blocks.map(block => {
              if (block === "feature") {
                return <FeatureBanners key={block} links={links} backdrop={featureBackdrop} scope={scope} {...itemActions} />;
              }
              if (block === "video") {
                return videoLinks.length
                  ? <VideoCards key={block} links={videoLinks} {...itemActions} />
                  : null;
              }
              if (block === "documents") {
                return documents.length || docLinks.length
                  ? <DocumentCards key={block} items={[...documents, ...docLinks]} {...itemActions} />
                  : null;
              }
              if (block === "images") {
                return mosaicImages.length ? (
                  <ImageMosaic
                    key={block}
                    images={mosaicImages}
                    dense={scope.layout !== "gallery"}
                    organizing={organizing}
                    onOpenImage={onOpenImage}
                    onReorder={onReorder}
                    {...itemActions}
                  />
                ) : null;
              }
              if (block === "links") {
                return rowLinks.length ? <LinkRows key={block} links={rowLinks} {...itemActions} /> : null;
              }
              return null;
            })}
          </div>
        )}
      </div>
    </section>
  );
}

function EmptyScope({ scope, organizing, inPackage }) {
  if (organizing) {
    return (
      <div className="border border-dashed border-border rounded-xl py-10 text-center">
        <scope.Icon className="w-6 h-6 mx-auto text-muted-foreground/40" />
        <p className="text-sm text-muted-foreground mt-3">{scope.emptyHint}</p>
        <p className="text-xs text-muted-foreground/60 mt-1">Drop files here, or use the buttons above.</p>
      </div>
    );
  }
  return (
    <p className="text-sm text-muted-foreground/70 py-2">
      {inPackage ? "Sold on this project — nothing uploaded yet." : "Not part of this project."}
    </p>
  );
}

/* ─────────────────────────────── stills ─────────────────────────────── */

function ImageMosaic({
  images, dense, organizing, onOpenImage, onReorder, onEditItem, onDeleteItem, onSetCover, heroImageId, mediaUrl,
}) {
  const [order, setOrder] = useState(images);
  const dragFrom = useRef(null);
  // The live order, readable from the drag handlers without waiting for a
  // re-render — dragend must commit what the user actually sees.
  const orderRef = useRef(images);

  useEffect(() => {
    setOrder(images);
    orderRef.current = images;
  }, [images]);

  const onDragStart = (index) => (e) => {
    dragFrom.current = index;
    e.dataTransfer.effectAllowed = "move";
    // Firefox needs some payload for a drag to start at all.
    e.dataTransfer.setData("text/plain", String(index));
  };

  const onDragEnter = (index) => () => {
    const from = dragFrom.current;
    if (from === null || from === index) return;
    const next = [...orderRef.current];
    next.splice(index, 0, ...next.splice(from, 1));
    orderRef.current = next;
    setOrder(next);
    dragFrom.current = index;
  };

  const onDragEnd = () => {
    dragFrom.current = null;
    const ids = orderRef.current.map(m => m.id);
    if (ids.join() !== images.map(m => m.id).join()) onReorder(ids);
  };

  const cols = dense
    ? "grid-cols-3 sm:grid-cols-4 lg:grid-cols-6"
    : "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4";

  return (
    <div className={`grid ${cols} gap-3`}>
      {order.map((m, index) => (
        <figure
          key={m.id}
          className="group relative aspect-[4/3] rounded-lg overflow-hidden bg-secondary"
          draggable={organizing}
          onDragStart={organizing ? onDragStart(index) : undefined}
          onDragEnter={organizing ? onDragEnter(index) : undefined}
          onDragOver={organizing ? (e) => e.preventDefault() : undefined}
          onDragEnd={organizing ? onDragEnd : undefined}
          data-testid={`image-tile-${m.id}`}
        >
          <button
            type="button"
            /* The viewer steps through exactly what this mosaic shows, in the
               order it's currently shown in. */
            onClick={() => onOpenImage(order, index)}
            className="absolute inset-0 w-full h-full"
            title={m.caption || m.title || "Open"}
          >
            <MosaicImage media={m} mediaUrl={mediaUrl} />
          </button>

          {m.id === heroImageId && (
            <span className="absolute top-2 left-2 micro-label px-2 py-0.5 rounded-full glass text-foreground">
              Cover
            </span>
          )}

          {m.caption && !organizing && (
            <figcaption className="absolute bottom-0 left-0 right-0 px-2.5 py-2 text-[11px] text-white opacity-0 group-hover:opacity-100 transition-smooth"
              style={{ background: "linear-gradient(to top, rgba(0,0,0,.7), transparent)" }}>
              {m.caption}
            </figcaption>
          )}

          {organizing && (
            <ItemActions
              item={m}
              onEditItem={onEditItem}
              onDeleteItem={onDeleteItem}
              onSetCover={m.id === heroImageId ? null : onSetCover}
              draggable
            />
          )}
        </figure>
      ))}
    </div>
  );
}

/** A still that can't be fetched shows a quiet placeholder rather than a broken
 *  frame — some older uploads have lost their stored file. */
function MosaicImage({ media, mediaUrl }) {
  return (
    <CoverImage
      sources={[mediaUrl(media, "thumb")]}
      alt={media.caption || media.title || ""}
      className="w-full h-full object-cover transition-transform duration-550 ease-brand group-hover:scale-[1.03]"
      fallback={
        <span className="w-full h-full flex flex-col items-center justify-center gap-1.5 text-muted-foreground/50">
          <ImageOff className="w-5 h-5" strokeWidth={1.25} />
          <span className="text-[10px] px-2 text-center truncate max-w-full">
            {media.title || "Image unavailable"}
          </span>
        </span>
      }
    />
  );
}

/* ─────────────────────────────── video ─────────────────────────────── */

function VideoCards({ links, organizing, onEditItem, onDeleteItem }) {
  if (!links.length) return null;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {links.map(l => {
        const { label, Icon } = getLinkMeta(l.media_type);
        return (
          <div key={l.id} className="group relative rounded-xl overflow-hidden border border-border card-hover" data-testid={`video-card-${l.id}`}>
            <a href={l.url} target="_blank" rel="noopener noreferrer" className="block">
              <div className="relative aspect-video bg-zinc-900">
                <CoverImage
                  sources={videoPosters(l.url || "")}
                  className="w-full h-full object-cover opacity-90 transition-smooth group-hover:opacity-100"
                />
                <span className="absolute inset-0 flex items-center justify-center">
                  <span className="w-12 h-12 rounded-full glass flex items-center justify-center">
                    <Play className="w-4 h-4 text-foreground ml-0.5" />
                  </span>
                </span>
              </div>
              <div className="px-4 py-3 bg-card">
                <p className="text-sm font-normal truncate">{l.title || label}</p>
                <p className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-1.5">
                  <Icon className="w-3 h-3" />
                  {linkHost(l.url || "")}
                </p>
              </div>
            </a>
            {organizing && <ItemActions item={l} onEditItem={onEditItem} onDeleteItem={onDeleteItem} />}
          </div>
        );
      })}
    </div>
  );
}

/* ──────────────────────────── decks & docs ─────────────────────────── */

function DocumentCards({ items, organizing, onEditItem, onDeleteItem, mediaUrl }) {
  if (!items.length) return null;

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
      {items.map(item => {
        const isFile = item.media_type === "document";
        const href = isFile ? mediaUrl(item) : item.url;
        const { label, Icon } = getLinkMeta(item.media_type);
        return (
          <div key={item.id} className="group relative rounded-xl border border-border overflow-hidden card-hover" data-testid={`doc-card-${item.id}`}>
            <a href={href} target="_blank" rel="noopener noreferrer" className="block">
              {/* A paper-proportioned face — a deck reads as a deck */}
              <div className="aspect-[3/4] bg-warm-100 flex flex-col items-center justify-center gap-2">
                <FileText className="w-7 h-7 text-warm-700/70" strokeWidth={1.25} />
                <span className="micro-label text-warm-700/70">{isFile ? "PDF" : label}</span>
              </div>
              <div className="px-3 py-2.5 bg-card border-t border-border">
                <p className="text-xs truncate">{item.title || item.file_name || label}</p>
                <p className="text-[10px] text-muted-foreground mt-0.5 flex items-center gap-1">
                  <Icon className="w-2.5 h-2.5" />
                  {isFile ? formatSize(item.file_size) : linkHost(item.url || "")}
                </p>
              </div>
            </a>
            {organizing && <ItemActions item={item} onEditItem={onEditItem} onDeleteItem={onDeleteItem} />}
          </div>
        );
      })}
    </div>
  );
}

/* ───────────────────── live platforms & websites ────────────────────── */

function FeatureBanners({ links, backdrop, scope, organizing, onEditItem, onDeleteItem, mediaUrl }) {
  if (!links.length) return null;
  const backdropUrl = backdrop ? mediaUrl(backdrop, "thumb") : null;

  return (
    <div className="space-y-4">
      {links.map((l, i) => {
        const { label, Icon } = getLinkMeta(l.media_type);
        return (
          <div
            key={l.id}
            className="group relative rounded-xl overflow-hidden border border-border"
            data-testid={`feature-card-${l.id}`}
          >
            <div className="relative aspect-[21/9] sm:aspect-[3/1] bg-zinc-900">
              {i === 0 && backdropUrl ? (
                <img src={backdropUrl} alt="" className="w-full h-full object-cover opacity-70 transition-transform duration-850 ease-brand group-hover:scale-[1.02]" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <scope.Icon className="w-8 h-8 text-white/20" strokeWidth={1.25} />
                </div>
              )}
              <div className="absolute inset-0" style={{ background: "linear-gradient(to right, rgba(0,0,0,.72), rgba(0,0,0,.15))" }} />

              <div className="absolute inset-0 flex flex-col justify-center px-6 sm:px-8">
                <span className="micro-label text-white/70">{scope.short}</span>
                <p className="text-white text-xl sm:text-2xl font-light mt-1.5">{l.title || label}</p>
                <p className="text-white/60 text-xs mt-1 flex items-center gap-1.5">
                  <Icon className="w-3 h-3" />
                  {linkHost(l.url || "")}
                </p>
                <a
                  href={l.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-4 inline-flex items-center gap-2 self-start px-5 h-10 rounded-full bg-white text-black text-[15px] tracking-[-0.25px] transition-smooth hover:opacity-[0.88] active:scale-[0.975]"
                  data-testid={`feature-open-${l.id}`}
                >
                  Open
                  <ArrowUpRight className="w-4 h-4" />
                </a>
              </div>
            </div>
            {organizing && <ItemActions item={l} onEditItem={onEditItem} onDeleteItem={onDeleteItem} light />}
          </div>
        );
      })}
    </div>
  );
}

/* ─────────────────────────────── links ─────────────────────────────── */

function LinkRows({ links, organizing, onEditItem, onDeleteItem }) {
  return (
    <div className="rounded-xl border border-border divide-y divide-border overflow-hidden">
      {links.map(l => {
        const { label, Icon } = getLinkMeta(l.media_type);
        return (
          <div key={l.id} className="group flex items-center justify-between gap-4 px-4 py-3 bg-card" data-testid={`link-row-${l.id}`}>
            <a href={l.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 min-w-0 flex-1">
              <Icon className="w-4 h-4 text-muted-foreground shrink-0" />
              <span className="text-sm truncate">{l.title || label}</span>
              <span className="text-[11px] text-muted-foreground truncate hidden sm:inline">{linkHost(l.url || "")}</span>
            </a>
            <div className="flex items-center gap-1 shrink-0">
              {organizing && (
                <>
                  <IconButton title="Edit" onClick={() => onEditItem(l)}><Pencil className="w-3.5 h-3.5" /></IconButton>
                  <IconButton title="Remove" onClick={() => onDeleteItem(l)} danger><Trash2 className="w-3.5 h-3.5" /></IconButton>
                </>
              )}
              <a
                href={l.url}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-full text-muted-foreground hover:text-foreground hover:bg-secondary transition-smooth"
                title="Open"
              >
                <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ─────────────────────────── shared chrome ─────────────────────────── */

function ItemActions({ item, onEditItem, onDeleteItem, onSetCover, draggable }) {
  return (
    <div className="absolute top-2 right-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-smooth z-10">
      {draggable && (
        <span
          className="p-1.5 rounded-full glass cursor-grab active:cursor-grabbing"
          title="Drag to reorder"
        >
          <GripVertical className="w-3.5 h-3.5 text-foreground/70" />
        </span>
      )}
      {onSetCover && (
        <IconButton title="Use as cover" glass onClick={() => onSetCover(item)}>
          <Star className="w-3.5 h-3.5" />
        </IconButton>
      )}
      <IconButton title="Edit" glass onClick={() => onEditItem(item)}>
        <Pencil className="w-3.5 h-3.5" />
      </IconButton>
      <IconButton title="Remove" glass danger onClick={() => onDeleteItem(item)}>
        <Trash2 className="w-3.5 h-3.5" />
      </IconButton>
    </div>
  );
}

function IconButton({ children, title, onClick, glass, danger }) {
  return (
    <button
      type="button"
      title={title}
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); onClick(); }}
      className={`p-1.5 rounded-full transition-smooth ${glass ? "glass" : "hover:bg-secondary"} ${
        danger ? "text-terracotta-600 hover:text-terracotta-700" : "text-muted-foreground hover:text-foreground"
      }`}
    >
      {children}
    </button>
  );
}

function formatSize(bytes) {
  if (!bytes) return "PDF";
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}
